import { useEffect, useMemo, useRef } from 'react';
import { ActionIcon, Group, ScrollArea, Stack, Text } from '@mantine/core';
import {
  IconChevronRight,
  IconChevronsDown,
  IconChevronsUp,
  IconEye,
  IconFolder,
  IconFolderOpen,
  IconLock,
  IconRefresh,
  IconUsersGroup,
  IconWorld,
} from '@tabler/icons-react';
import { Tree, useTree, type TreeNodeData, type RenderTreeNodePayload } from '@mantine/core';
import type { Folder } from '../../../schemas/folder.schema';
import { FolderActionsMenu } from './FolderActionsMenu';
import { ZoneSubjectAvatars, ZoneOwnerAvatar } from './DriveZoneMembers';
import type { ZoneSubject } from './useZoneMembers';
import {
  folderIconColor,
  getEffectiveAccess,
  isDriveAccess,
  DRIVE_ACCESS_LABEL,
  type DriveAccess,
} from './driveAccess';
import classes from './FolderTree.module.css';

/** Значок явного режима доступа (узел дерева, плитка папки). */
export function AccessIcon({ access }: { access: DriveAccess }) {
  const title = DRIVE_ACCESS_LABEL[access];
  if (access === 'private') {
    return <IconLock size={12} color='var(--mantine-color-grape-6)' title={title} />;
  }
  if (access === 'public_read') {
    return <IconEye size={12} color='var(--mantine-color-orange-6)' title={title} />;
  }
  if (access === 'custom') {
    return <IconUsersGroup size={12} color='var(--mantine-color-indigo-6)' title={title} />;
  }
  return <IconWorld size={12} color='var(--mantine-color-blue-5)' title={title} />;
}

/** value узла дерева ↔ id папки. */
export function folderNodeValue(folderId: number): string {
  return `folder-${folderId}`;
}
export function parseFolderNodeValue(value: string): number | null {
  const id = parseInt(value.replace('folder-', ''), 10);
  return Number.isNaN(id) ? null : id;
}

/**
 * Плоский список папок → TreeNodeData.
 *
 * По умолчанию корни дерева — папки с `parent_id NULL` (весь Диск).
 * Если задан `rootId`, дерево ограничено ОДНИМ поддеревом: его вершиной
 * становится сама папка `rootId`, а выше неё подняться нельзя — режим
 * встраиваемой панели (папка проекта на карточке сущности).
 *
 * @param folders плоский список папок (обычно весь видимый Диск)
 * @param rootId вершина поддерева; null/undefined — весь Диск
 */
export function buildFolderTreeData(folders: Folder[], rootId?: number | null): TreeNodeData[] {
  const childrenByParent = new Map<number | null, Folder[]>();
  for (const folder of folders) {
    const key = folder.parent_id ?? null;
    const bucket = childrenByParent.get(key);
    if (bucket) bucket.push(folder);
    else childrenByParent.set(key, [folder]);
  }
  const sortByName = (a: Folder, b: Folder) => a.name.localeCompare(b.name, 'ru');

  const toNode = (folder: Folder): TreeNodeData => {
    const children = (childrenByParent.get(folder.id) ?? []).slice().sort(sortByName);
    const node: TreeNodeData = {
      value: folderNodeValue(folder.id),
      label: folder.name,
    };
    if (children.length > 0) node.children = children.map(toNode);
    return node;
  };

  if (rootId != null) {
    const root = folders.find(folder => folder.id === rootId);
    return root ? [toNode(root)] : [];
  }

  return (childrenByParent.get(null) ?? []).slice().sort(sortByName).map(toNode);
}

interface FolderTreeProps {
  folders: Folder[];
  /** Активная папка (подсветка). */
  activeFolderId: number | null;
  /**
   * Ограничить дерево одним поддеревом: вершина — папка `rootId`, выше не
   * поднимаемся (встраиваемая панель). Не задан — весь Диск, как на странице.
   */
  rootId?: number | null;
  /** Текущий юзер и админ-признак — для доступности пунктов меню (нужны, только если меню показывается). */
  currentUserId?: number | null;
  isAdmin?: boolean;
  onSelect: (folderId: number) => void;
  /** Перечитать папки (дерево не видит чужие/серверные изменения до refresh). */
  onRefresh: () => void;
  /**
   * Read-only режим (файл-пикер): скрыть hover-меню управления папками —
   * дерево служит только навигацией. Управляющие on*-хендлеры при этом не нужны.
   */
  hideActions?: boolean;
  onCreateChild?: (parentFolderId: number) => void;
  onRename?: (folder: Folder) => void;
  onMove?: (folder: Folder) => void;
  onDelete?: (folder: Folder) => void;
  onChangeAccess?: (folder: Folder) => void;
  /**
   * Состав custom-зон (useZoneMembersMap): на корнях зон вместо значка режима —
   * аватарки субъектов ACL, групп и людей (2 + «+N»). Не задан (пикеры) —
   * значок AccessIcon.
   */
  zoneMembersById?: Map<number, ZoneSubject[]>;
  /**
   * На сколько уровней раскрыть дерево при первом показе: 1 — дети вершины
   * видны сразу (дефолт страницы Диска), 0 — только вершина. Работает ОДИН раз
   * на монтирование: последующие сворачивания юзера не отменяются.
   */
  defaultExpandedDepth?: number;
}

/**
 * FolderTree — дерево папок Диска (Mantine Tree, эталон CatalogTreeView).
 * Hover-меню узла: «Новая папка» — по праву записи в зону;
 * «Переименовать/Переместить/Доступ/Удалить» — скрыты у системных папок,
 * задизейблены без прав по матрице зоны (public_write — автор,
 * public_read/private — владелец зоны, ADMIN — всё); сервер всё равно
 * authoritative. Значок на узле — явный режим доступа (корень прав зоны).
 */
export function FolderTree({
  folders,
  activeFolderId,
  rootId,
  currentUserId,
  isAdmin,
  onSelect,
  onRefresh,
  hideActions,
  onCreateChild,
  onRename,
  onMove,
  onDelete,
  onChangeAccess,
  zoneMembersById,
  defaultExpandedDepth = 0,
}: FolderTreeProps) {
  const tree = useTree();
  // Мемоизация обязательна: Tree синхронизирует внутреннее состояние с data —
  // новая ссылка на каждый рендер даёт бесконечный цикл обновлений
  const treeData = useMemo(() => buildFolderTreeData(folders, rootId), [folders, rootId]);
  const foldersById = useMemo(() => new Map(folders.map(folder => [folder.id, folder])), [folders]);

  // Диплинки (?folder=N): раскрываем предков активной папки, чтобы узел был
  // виден в дереве. Сам узел и ручные сворачивания юзера не трогаем.
  // tree в deps не включаем: его методы нестабильны между рендерами (Mantine),
  // а expand меняет state — попал бы в цикл.
  useEffect(() => {
    // Вершина поддерева раскрыта всегда: иначе встраиваемая панель открывается
    // одной свёрнутой строкой, и подпапок не видно
    if (rootId != null) tree.expand(folderNodeValue(rootId));
    if (activeFolderId == null) return;
    const active = foldersById.get(activeFolderId);
    let cursor = active?.parent_id != null ? foldersById.get(active.parent_id) : undefined;
    const visited = new Set<number>();
    while (cursor && !visited.has(cursor.id)) {
      visited.add(cursor.id);
      tree.expand(folderNodeValue(cursor.id));
      cursor = cursor.parent_id != null ? foldersById.get(cursor.parent_id) : undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFolderId, foldersById, rootId]);

  /**
   * Стартовое раскрытие на N уровней — ОДИН раз, когда папки доехали. Флаг
   * обязателен: без него любой reload Диска (создание/переименование папки)
   * снова разворачивал бы то, что юзер свернул руками.
   */
  const initiallyExpanded = useRef(false);
  useEffect(() => {
    if (initiallyExpanded.current || defaultExpandedDepth <= 0) return;
    if (treeData.length === 0) return; // папки ещё грузятся
    initiallyExpanded.current = true;

    let level: TreeNodeData[] = treeData;
    for (let depth = 0; depth < defaultExpandedDepth && level.length > 0; depth++) {
      for (const node of level) tree.expand(node.value);
      level = level.flatMap(node => node.children ?? []);
    }
    // tree в deps не включаем — методы Mantine нестабильны между рендерами
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeData, defaultExpandedDepth]);

  const renderNode = ({ node, expanded, hasChildren, elementProps }: RenderTreeNodePayload) => {
    const { style, onClick, className: nodeClassName, ...rest } = elementProps;
    const folderId = parseFolderNodeValue(node.value);
    const folder = folderId != null ? foldersById.get(folderId) : undefined;
    if (!folder) return null;

    const isActive = folder.id === activeFolderId;
    const isRoot = folder.parent_id == null;

    // Как в Google Drive: клик по строке — только выбор (грид справа),
    // сворачивание/разворачивание — только кликом по шеврону
    const handleRowClick = () => {
      onSelect(folder.id);
    };
    const handleChevronClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      onClick?.(e); // Mantine Tree: toggle expanded
    };

    const Icon = expanded && hasChildren ? IconFolderOpen : IconFolder;
    // Иконка личной зоны (эффективно private) — в тон замочку
    const iconColor = folderIconColor(getEffectiveAccess(folder, foldersById));

    return (
      <Group
        gap={6}
        {...rest}
        className={[nodeClassName, classes.node].filter(Boolean).join(' ')}
        onClick={handleRowClick}
        style={{
          ...style,
          paddingTop: 4,
          paddingBottom: 4,
          borderRadius: 4,
          backgroundColor: isActive ? 'var(--mantine-color-blue-0)' : undefined,
        }}
      >
        {hasChildren ? (
          // Классика деревьев: вправо — свёрнуто, вниз — развёрнуто
          <IconChevronRight
            size={16}
            onClick={handleChevronClick}
            style={{
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          />
        ) : (
          <div style={{ width: 16 }} />
        )}
        <Icon size={16} color={iconColor} />
        <Text size='sm' style={{ flex: 1 }} truncate>
          {node.label}
        </Text>
        {/* Корень custom-зоны: аватарки субъектов ACL — групп и людей (2 + «+N»)
            вместо значка режима; без данных (пикеры) — обычный AccessIcon */}
        {folder.access === 'custom' &&
        !isRoot &&
        (zoneMembersById?.get(folder.id)?.length ?? 0) > 0 ? (
          <ZoneSubjectAvatars subjects={zoneMembersById?.get(folder.id) ?? []} max={2} size={18} />
        ) : (
          isDriveAccess(folder.access) && !isRoot && <AccessIcon access={folder.access} />
        )}
        {/* public_read: после глазика — владелец зоны (кто может записывать) */}
        {folder.access === 'public_read' && !isRoot && folder.created_by_id != null && (
          <ZoneOwnerAvatar userId={folder.created_by_id} size={18} />
        )}
        {folder.is_system && !isRoot && <IconLock size={12} color='var(--mantine-color-gray-5)' />}
        {!hideActions && onCreateChild && onRename && onMove && onDelete && onChangeAccess && (
          <div className={classes.actions} style={{ display: 'flex' }}>
            <FolderActionsMenu
              folder={folder}
              foldersById={foldersById}
              currentUserId={currentUserId ?? null}
              isAdmin={isAdmin ?? false}
              onCreateChild={onCreateChild}
              onRename={onRename}
              onMove={onMove}
              onDelete={onDelete}
              onChangeAccess={onChangeAccess}
              onRefresh={onRefresh}
            />
          </div>
        )}
      </Group>
    );
  };

  return (
    <Stack gap='xs' h='100%'>
      <Group gap='xs' justify='space-between' wrap='nowrap'>
        <Text fw={600} size='sm'>
          Папки
        </Text>
        <Group gap={2} wrap='nowrap'>
          <ActionIcon size='sm' variant='subtle' color='gray' title='Обновить' onClick={onRefresh}>
            <IconRefresh size={14} />
          </ActionIcon>
          <ActionIcon
            size='sm'
            variant='subtle'
            color='gray'
            title='Развернуть все'
            onClick={() => tree.expandAllNodes()}
          >
            <IconChevronsDown size={14} />
          </ActionIcon>
          <ActionIcon
            size='sm'
            variant='subtle'
            color='gray'
            title='Свернуть все'
            onClick={() => tree.collapseAllNodes()}
          >
            <IconChevronsUp size={14} />
          </ActionIcon>
        </Group>
      </Group>
      {/* offsetScrollbars: полоса прокрутки не перекрывает «три точки» узла */}
      <ScrollArea style={{ flex: 1 }} scrollHideDelay={0} offsetScrollbars>
        {/* Без selectOnClick: подсветка активного узла — своя (isActive),
            иначе Mantine красит серым узел последнего клика по шеврону */}
        {treeData.length > 0 ? (
          <Tree data={treeData} tree={tree} renderNode={renderNode} levelOffset={20} />
        ) : (
          <Text size='sm' c='dimmed' ta='center' py='xl'>
            Папок нет
          </Text>
        )}
      </ScrollArea>
    </Stack>
  );
}
